import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiService from 'services/apiService';
import {
    GPADashboardFilter, GPADashboardResponse, GPADistributionDto,
    GradeLevelDashboardFilter, GradeLevelDashboardResponse, GradeLevelDistributionDto,
    StudentServiceDashboardFilter, StudentServiceDashboardResponse, StudentServiceDistributionDto,
    StudentServiceYearTotalDto,
} from 'generated/openApi';
import { StudentServiceMetric } from '../types';

interface DashboardYearFilter { schoolYears: number[]; }
interface DashboardYearTotal { year: number; totalStudents: number; }
interface DashboardGrandTotal { totalStudents: number; }

interface DistributionContextValue<T, F extends DashboardYearFilter, Y extends DashboardYearTotal = DashboardYearTotal> {
    data: T[]; loading: boolean; error: string | null; filter: F;
    activeSchoolYear: number | null; totalsByYear: Y[]; grandTotal: DashboardGrandTotal | null;
    updateFilter: (updates: Partial<F>) => void; refetch: () => void;
}

function useDistributionData<T, F extends DashboardYearFilter, R extends { activeSchoolYear: number; data: T[]; totalsByYear: any[]; grandTotal: DashboardGrandTotal }, Y extends DashboardYearTotal>(
    initialFilter: F, request: (filter: F) => Promise<R>, errorMessage: string
): DistributionContextValue<T, F, Y> {
    const [data, setData] = useState<T[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<Y[]>([]);
    const [grandTotal, setGrandTotal] = useState<DashboardGrandTotal | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<F>(initialFilter);
    const [initialized, setInitialized] = useState(false);

    const fetchData = async () => {
        setLoading(true); setError(null);
        try {
            const responseData = await request(filter);
            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);
                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(previous => ({ ...previous, schoolYears: [responseData.activeSchoolYear] }));
                    setInitialized(true);
                    return;
                }
            }
            setData(responseData.data || []);
            setTotalsByYear((responseData.totalsByYear || []) as Y[]);
            setGrandTotal(responseData.grandTotal || null);
        } catch (requestError) {
            console.error(errorMessage, requestError); setError(errorMessage); setData([]);
        } finally { setLoading(false); }
    };

    // The request function intentionally follows the context's current state;
    // matching the established dashboard-provider fetch lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (initialized) fetchData(); }, [filter]);

    return {
        data, loading, error, filter, activeSchoolYear, totalsByYear, grandTotal,
        updateFilter: updates => setFilter(previous => ({ ...previous, ...updates })),
        refetch: fetchData,
    };
}

const GPAContext = createContext<DistributionContextValue<GPADistributionDto, GPADashboardFilter> | undefined>(undefined);
export const GPADataProvider = ({ children }: { children: React.ReactNode }) => (
    <GPAContext.Provider value={useDistributionData<GPADistributionDto, GPADashboardFilter, GPADashboardResponse, DashboardYearTotal>(
        { schoolYears: [] }, filter => ApiService.gpaDashboard.getGPADistribution(filter), 'Failed to load GPA data')}>
        {children}
    </GPAContext.Provider>
);
export const useGPAData = () => {
    const value = useContext(GPAContext); if (!value) throw new Error('useGPAData must be used within GPADataProvider'); return value;
};

const GradeLevelContext = createContext<DistributionContextValue<GradeLevelDistributionDto, GradeLevelDashboardFilter> | undefined>(undefined);
export const GradeLevelDataProvider = ({ children }: { children: React.ReactNode }) => (
    <GradeLevelContext.Provider value={useDistributionData<GradeLevelDistributionDto, GradeLevelDashboardFilter, GradeLevelDashboardResponse, DashboardYearTotal>(
        { schoolYears: [] }, filter => ApiService.gradeLevelDashboard.getGradeLevelDistribution(filter), 'Failed to load grade-level data')}>
        {children}
    </GradeLevelContext.Provider>
);
export const useGradeLevelData = () => {
    const value = useContext(GradeLevelContext); if (!value) throw new Error('useGradeLevelData must be used within GradeLevelDataProvider'); return value;
};

interface StudentServiceContextValue extends DistributionContextValue<StudentServiceDistributionDto, StudentServiceDashboardFilter, StudentServiceYearTotalDto> {
    metric: StudentServiceMetric; setMetric: (metric: StudentServiceMetric) => void;
}
const StudentServiceContext = createContext<StudentServiceContextValue | undefined>(undefined);
export const StudentServiceDataProvider = ({ children }: { children: React.ReactNode }) => {
    const distribution = useDistributionData<StudentServiceDistributionDto, StudentServiceDashboardFilter, StudentServiceDashboardResponse, StudentServiceYearTotalDto>(
        { schoolYears: [] }, filter => ApiService.studentServiceDashboard.getStudentServiceDistribution(filter), 'Failed to load student-service data');
    const [metric, setMetric] = useState<StudentServiceMetric>('participantRate');
    return <StudentServiceContext.Provider value={{ ...distribution, metric, setMetric }}>{children}</StudentServiceContext.Provider>;
};
export const useStudentServiceData = () => {
    const value = useContext(StudentServiceContext); if (!value) throw new Error('useStudentServiceData must be used within StudentServiceDataProvider'); return value;
};
